import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api';

const STEPS = { AMOUNT: 'amount', KYC: 'kyc', PAYMENT: 'payment', SUCCESS: 'success' };

export default function CrossmintOnrampWidget({ onClose }) {
  const [step, setStep] = useState(STEPS.AMOUNT);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const pollRef = useRef(null);
  const checkoutContainerRef = useRef(null);
  const checkoutMountedRef = useRef(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Step 1: Create order
  const handleCreateOrder = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post('/crossmint/create-order', { amount: parseFloat(amount) });
      setOrder(data);

      const paymentStatus = data.payment?.status;

      if (paymentStatus === 'requires-kyc' || paymentStatus === 'crypto-payer-insufficient-funds') {
        // KYC needed — load Persona
        setStep(STEPS.KYC);
        startPolling(data.orderId || data.id);
      } else if (paymentStatus === 'awaiting-payment') {
        // KYC already done, go straight to payment
        setStep(STEPS.PAYMENT);
      } else {
        // Unexpected status — show payment step anyway
        setStep(STEPS.PAYMENT);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating order');
    } finally {
      setLoading(false);
    }
  };

  // Poll order status (used during KYC step)
  const startPolling = useCallback((orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/crossmint/order/${orderId}`);
        setOrder(data);

        const status = data.payment?.status;
        if (status === 'awaiting-payment') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep(STEPS.PAYMENT);
        } else if (status === 'completed' || status === 'succeeded') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep(STEPS.SUCCESS);
        } else if (status === 'failed' || status === 'expired') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError(`Order ${status}. Please try again.`);
        }
      } catch {
        // Silently retry on poll failure
      }
    }, 3000);
  }, []);

  // Step 2: KYC via Persona
  const personaRef = useRef(null);
  useEffect(() => {
    if (step !== STEPS.KYC || !order) return;

    const kycData = order.payment?.preparation?.kyc;
    if (!kycData) return;

    const loadPersona = async () => {
      try {
        const Persona = await import('persona');
        const client = new Persona.Client({
          templateId: kycData.templateId,
          referenceId: kycData.referenceId,
          environmentId: kycData.environmentId,
          onComplete: () => {
            // KYC done — polling will pick up the status change
          },
          onCancel: () => {
            setError('KYC verification cancelled');
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          },
          onError: (err) => {
            console.error('Persona error:', err);
            setError('KYC verification error. Please try again.');
          },
        });

        personaRef.current = client;
        client.open();
      } catch (err) {
        console.error('Failed to load Persona SDK:', err);
        setError('Failed to load identity verification. Please try again.');
      }
    };

    loadPersona();

    return () => {
      personaRef.current?.destroy?.();
    };
  }, [step, order]);

  // Step 3: Payment via Checkout.com Flow
  useEffect(() => {
    if (step !== STEPS.PAYMENT || !order || checkoutMountedRef.current) return;

    const paymentSession = order.payment?.preparation?.checkoutcomPaymentSession;
    const publicKey = order.payment?.preparation?.checkoutcomPublicKey;
    if (!paymentSession || !publicKey) return;

    // Wait for Checkout.com script to be available
    const initCheckout = () => {
      if (!window.CheckoutWebComponents) {
        setTimeout(initCheckout, 500);
        return;
      }

      const mount = async () => {
        try {
          const checkout = await window.CheckoutWebComponents({
            paymentSession,
            publicKey,
            environment: 'production',
            onPaymentCompleted: (_component, paymentResponse) => {
              console.log('Checkout.com payment completed:', paymentResponse);
              setStep(STEPS.SUCCESS);
            },
            onError: (_component, err) => {
              console.error('Checkout.com error:', err);
              setError('Payment failed. Please try again.');
            },
          });

          const flow = checkout.create('flow');
          if (checkoutContainerRef.current) {
            flow.mount(checkoutContainerRef.current);
            checkoutMountedRef.current = true;
          }
        } catch (err) {
          console.error('Checkout.com init error:', err);
          setError('Failed to load payment form. Please try again.');
        }
      };

      mount();
    };

    initCheckout();
  }, [step, order]);

  // Reset checkout mounted flag when leaving payment step
  useEffect(() => {
    if (step !== STEPS.PAYMENT) {
      checkoutMountedRef.current = false;
    }
  }, [step]);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <strong>
            {step === STEPS.AMOUNT && 'Buy USDC with Crossmint'}
            {step === STEPS.KYC && 'Identity Verification'}
            {step === STEPS.PAYMENT && 'Card Payment'}
            {step === STEPS.SUCCESS && 'Purchase Complete'}
          </strong>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div style={{ padding: '24px' }}>
          {error && (
            <div className="wallet-deposit-error" style={{ marginBottom: 16 }}>{error}</div>
          )}

          {/* Step 1: Amount */}
          {step === STEPS.AMOUNT && (
            <>
              <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
                Purchase USDC on Base with your credit or debit card via Crossmint. Available globally.
              </p>
              <div className="wallet-deposit-row">
                <input
                  type="number"
                  step="1"
                  min="5"
                  placeholder="Amount in USD"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="wallet-deposit-input"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateOrder()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleCreateOrder}
                  disabled={loading || !amount || parseFloat(amount) <= 0}
                >
                  {loading ? 'Creating...' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {/* Step 2: KYC */}
          {step === STEPS.KYC && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
                First-time verification required. Complete the identity check in the popup window.
              </p>
              <div className="loading">Waiting for verification...</div>
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: 16 }}>
                This window will update automatically once verified.
              </p>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === STEPS.PAYMENT && (
            <>
              <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
                Enter your card details to complete the purchase of ${amount} USDC.
              </p>
              <div ref={checkoutContainerRef} style={{ minHeight: 200 }} />
            </>
          )}

          {/* Step 4: Success */}
          {step === STEPS.SUCCESS && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: 16 }}>&#10003;</div>
              <p style={{ color: '#b0b0b0', marginBottom: 8 }}>
                Payment successful! Your USDC will be deposited automatically.
              </p>
              <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 24 }}>
                This may take a few minutes. Your balance will update once confirmed.
              </p>
              <button className="btn btn-primary" onClick={onClose} style={{ width: '100%', padding: '14px' }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
